import { useMemo, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, CalendarDays, CreditCard, FileText, KeyRound, Loader2, Mail, MapPin, Phone, Users } from 'lucide-react'
import { toast } from 'sonner'

import OrgUserAddressManager from '@/components/common/org-user-address-manager'
import ResetPasswordDialog from '@/components/users/reset-password-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/components/ui/empty'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { CompactTableDate, commerceTableClassName } from '@/components/common/compact-table-date'
import { DataTablePagination } from '@/components/common/data-table-pagination'
import { orgUsersApi, type OrgUser, type OrgUserAddress } from '@/services/org-users'
import { orgUserAdminApi } from '@/services/org-user-admin'
import { ordersApi } from '@/services/orders'
import { getErrorMessage } from '@/lib/api'
import { buildAddressAccessSummary, formatTimeLabel, getAddressTypeLabel, USER_ADDRESS_TYPE } from '@/lib/user-address'
import { normalizeOrgUserGroupKey } from '@/lib/org-user-groups'
import { withSystemRoleCapabilityFallback } from '@/lib/route-access'
import { getServiceInstanceIdFromSearch, withServiceInstanceId } from '@/lib/service-instance'
import { buildJobsListPath } from '@/lib/jobs-list-navigation'
import { formatCurrency } from '@/lib/utils'
import { salesApi } from '@/services/sales'
import { useAuthStore } from '@/stores/auth-store'
import type { StoreOrderSummary } from '@/types'

type CustomerDirectoryContext = 'bookings' | 'jobs'
type CustomerTabKey = 'retail' | 'business'

type CustomerDetailPageProps = {
  context: CustomerDirectoryContext
}

const firstPresent = (...values: Array<string | number | null | undefined>) => {
  for (const value of values) {
    const text = String(value ?? '').trim()
    if (text) {
      return text
    }
  }
  return '-'
}

const normalizeCustomerTab = (value: string | null | undefined): CustomerTabKey => {
  return String(value ?? '').trim().toLowerCase() === 'business' ? 'business' : 'retail'
}

const getCustomerDisplayName = (user: {
  title?: string | null
  fname?: string | null
  mname?: string | null
  lname?: string | null
  username?: string | null
  usr_reg_num?: string | null
}) => {
  const name = [user.title, user.fname, user.mname, user.lname]
    .map((part) => String(part ?? '').trim())
    .filter(Boolean)
    .join(' ')
    .trim()

  const baseName = name || String(user.username ?? '').trim() || 'Unknown customer'
  const regNum = String(user.usr_reg_num ?? '').trim()
  if (!regNum || regNum === '0') {
    return baseName
  }

  return `${baseName} (${regNum})`
}

const getStatusBadgeClassName = (status?: string | null) => {
  const normalized = String(status ?? '').trim().toLowerCase()
  if (normalized === 'inactive' || normalized === '0') {
    return 'border-rose-200 bg-rose-50 text-rose-700'
  }
  if (normalized === 'active' || normalized === '1') {
    return 'border-emerald-200 bg-emerald-50 text-emerald-700'
  }
  return 'border-slate-200 bg-slate-50 text-slate-700'
}

const toAddressArray = (addresses?: OrgUser['address_data']): OrgUserAddress[] => {
  if (Array.isArray(addresses)) {
    return addresses
  }
  if (addresses) {
    return [addresses]
  }
  return []
}

const getPrimaryAddress = (customer: OrgUser): OrgUserAddress | null => {
  const addresses = toAddressArray(customer.address_data)
  return addresses.find((address) => address.default) ?? addresses[0] ?? null
}

const formatAddress = (address: OrgUserAddress | null) => {
  if (!address) {
    return '-'
  }

  const line = [
    address.door_no,
    address.building_name,
    address.floor_line,
    address.street_line,
    address.city_name,
    address.state_name,
    address.country_name,
    address.zipcode,
  ]
    .map((part) => String(part ?? '').trim())
    .filter(Boolean)
    .join(', ')

  return line || '-'
}

const getOrderTypeLabel = (order: StoreOrderSummary) => (
  order.order_type_label
  || order.type_label
  || order.serve_type_label
  || order.order_type_key
  || 'Booking'
)

const getOrderStatusLabel = (order: StoreOrderSummary) => (
  order.status_label || String(order.status ?? 'Unknown')
)

export default function CustomerDetailPage({ context }: CustomerDetailPageProps) {
  const { customerId } = useParams<{ customerId: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const serviceInstanceId = getServiceInstanceIdFromSearch(location.search)
    || useAuthStore((state) => state.serviceInstanceId)
  const currentUserId = useAuthStore((state) => state.user?.id)
  const currentPost = useAuthStore((state) => state.currentPost)
  const currentCapabilities = useAuthStore((state) => state.currentPost?.capabilities)
  const rolePermissionCapabilities = useMemo(() => {
    const permissions = currentPost?.roles_data?.permissions
    if (!permissions || typeof permissions !== 'object') {
      return undefined
    }

    const normalized: Record<string, boolean> = {}
    for (const [key, value] of Object.entries(permissions as Record<string, unknown>)) {
      normalized[key] = value === true || value === 1 || String(value).toLowerCase() === 'true'
    }
    return normalized
  }, [currentPost?.roles_data?.permissions])
  const effectiveCapabilities = useMemo(() => {
    if (!currentCapabilities && !rolePermissionCapabilities) {
      return withSystemRoleCapabilityFallback(currentPost, undefined)
    }

    return withSystemRoleCapabilityFallback(currentPost, {
      ...(rolePermissionCapabilities ?? {}),
      ...(currentCapabilities ?? {}),
    })
  }, [currentCapabilities, currentPost, rolePermissionCapabilities])
  const canForcePasswordResetUsers = Boolean(effectiveCapabilities?.can_force_password_reset_users)
  const canCreateCustomerAddresses = Boolean(effectiveCapabilities?.can_create_user_addresses)
  const canUpdateCustomerAddresses = Boolean(effectiveCapabilities?.can_update_user_addresses)
  const canDeleteCustomerAddresses = Boolean(effectiveCapabilities?.can_remove_user_addresses)
  const canSetDefaultCustomerAddresses = Boolean(effectiveCapabilities?.can_set_default_user_addresses)
  const searchParams = new URLSearchParams(location.search)
  const activeTab = normalizeCustomerTab(searchParams.get('tab'))
  const requestedUserGroupId = searchParams.get('user_group_id')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [isResetPasswordDialogOpen, setIsResetPasswordDialogOpen] = useState(false)

  const groupsQuery = useQuery({
    queryKey: ['customer-detail-groups', serviceInstanceId],
    queryFn: () => orgUsersApi.listGroups({ serviceInstanceId }),
    enabled: Boolean(serviceInstanceId && !requestedUserGroupId),
    staleTime: 60_000,
  })

  const activeGroupId = useMemo(() => {
    if (requestedUserGroupId) {
      return requestedUserGroupId
    }

    const targetKey = activeTab === 'business' ? 'business_customer' : 'retail_customer'
    const match = (groupsQuery.data ?? []).find((group) => {
      const codeKey = normalizeOrgUserGroupKey(group.unique_code)
      const titleKey = normalizeOrgUserGroupKey(group.title)
      return codeKey === targetKey || titleKey === targetKey
    })
    return match?.id ?? null
  }, [activeTab, groupsQuery.data, requestedUserGroupId])

  const customerGroupIds = useMemo(() => {
    const ids = new Set<string>()
    if (activeGroupId) {
      ids.add(String(activeGroupId))
    }

    for (const group of groupsQuery.data ?? []) {
      const codeKey = normalizeOrgUserGroupKey(group.unique_code)
      const titleKey = normalizeOrgUserGroupKey(group.title)
      if (codeKey === 'retail_customer' || codeKey === 'business_customer' || titleKey === 'retail_customer' || titleKey === 'business_customer') {
        ids.add(String(group.id))
      }
    }

    return Array.from(ids)
  }, [activeGroupId, groupsQuery.data])

  const customerQuery = useQuery({
    queryKey: ['customer-detail', customerId, serviceInstanceId, customerGroupIds],
    queryFn: async () => {
      const groupsToTry = customerGroupIds.length > 0 ? customerGroupIds : [null]
      let lastError: unknown = null

      for (const userGroupId of groupsToTry) {
        try {
          return await orgUsersApi.getUser(customerId ?? '', {
            serviceInstanceId,
            userGroupId,
            skipAuthRedirectOn401: true,
          })
        } catch (error) {
          lastError = error
        }
      }

      throw lastError
    },
    enabled: Boolean(customerId && serviceInstanceId && (requestedUserGroupId || !groupsQuery.isLoading)),
    staleTime: 20_000,
  })

  const rawUserId = customerQuery.data?.user_id

  const linkedEnquiriesQuery = useQuery({
    queryKey: ['customer-linked-enquiries', rawUserId, serviceInstanceId],
    queryFn: () => salesApi.listEnquiries({
      service_instance_id: serviceInstanceId,
      customer_user_id: rawUserId,
    }),
    enabled: Boolean(rawUserId && serviceInstanceId),
    staleTime: 30_000,
  })

  const linkedEnquiries = linkedEnquiriesQuery.data?.results ?? []

  const ordersQuery = useQuery({
    queryKey: ['customer-detail-orders', rawUserId, serviceInstanceId, page, pageSize],
    queryFn: () => ordersApi.listOrders({
      page,
      page_size: pageSize,
      user_id: rawUserId,
      service_instance_id: serviceInstanceId,
      ordering: '-order_time',
    }),
    enabled: Boolean(rawUserId && serviceInstanceId),
    staleTime: 20_000,
  })

  const orders = ordersQuery.data?.results ?? []
  const totalOrders = ordersQuery.data?.count ?? 0
  const totalValue = useMemo(
    () => orders.reduce((sum, order) => {
      const amount = Number(order.total ?? 0)
      return Number.isFinite(amount) ? sum + amount : sum
    }, 0),
    [orders],
  )

  const resetPasswordMutation = useMutation({
    mutationFn: (password: string) => {
      const userId = customerQuery.data?.user_id ?? customerQuery.data?.id
      if (!userId) {
        throw new Error('Customer user id is unavailable.')
      }
      return orgUserAdminApi.forcePasswordReset(userId, serviceInstanceId, password)
    },
    onSuccess: async () => {
      toast.success('Password reset successfully. The customer must change it at next sign in.')
      setIsResetPasswordDialogOpen(false)
      await queryClient.invalidateQueries({ queryKey: ['customer-detail', customerId, serviceInstanceId] })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const buildDirectoryLink = () => {
    const tab = new URLSearchParams(location.search).get('tab')
    const params = new URLSearchParams()
    if (serviceInstanceId) {
      params.set('service_instance_id', String(serviceInstanceId))
    }
    if (activeGroupId) {
      params.set('user_group_id', String(activeGroupId))
    }
    if (tab) {
      params.set('tab', tab)
    }
    const basePath = context === 'bookings' ? '/services/customers' : '/job/customers'
    const search = params.toString()
    return `${basePath}${search ? `?${search}` : ''}`
  }

  const buildOrderDetailLink = (order: StoreOrderSummary) => {
    const referenceId = order.reference_id || order.id
    return withServiceInstanceId(`/services/orders/${referenceId}`, serviceInstanceId)
  }

  const buildBookingsLink = (scope: 'open' | 'completed') => {
    if (!rawUserId) {
      return withServiceInstanceId('/services/orders', serviceInstanceId)
    }

    const params = new URLSearchParams()
    params.set('customer_id', String(rawUserId))
    params.set('booking_scope', scope)
    return withServiceInstanceId(`/services/orders?${params.toString()}`, serviceInstanceId)
  }

  const buildJobsLink = (scope: 'open' | 'completed') => {
    return withServiceInstanceId(
      buildJobsListPath({
        serviceInstanceId,
        clientUser: rawUserId,
        jobScope: scope,
      }),
      serviceInstanceId,
    )
  }

  const buildEnquiryLink = (enquiryId: number) => {
    const params = new URLSearchParams()
    if (serviceInstanceId) {
      params.set('service_instance_id', String(serviceInstanceId))
    }
    params.set('enquiry_id', String(enquiryId))
    return `/sales/enquiries?${params.toString()}`
  }

  if (customerQuery.isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 p-10 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading customer...
      </div>
    )
  }

  if (!customerQuery.data) {
    return (
      <div className="p-6">
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon"><Users /></EmptyMedia>
            <EmptyTitle>Customer not found</EmptyTitle>
            <EmptyDescription>The selected customer could not be loaded.</EmptyDescription>
          </EmptyHeader>
          <Button variant="outline" onClick={() => navigate(buildDirectoryLink())}>Back to customers</Button>
        </Empty>
      </div>
    )
  }

  const customer = customerQuery.data
  const isBusinessCustomer = activeTab === 'business'
    || normalizeOrgUserGroupKey(customer.user_group_code) === 'business_customer'
    || normalizeOrgUserGroupKey(customer.user_group_title) === 'business_customer'
  const primaryAddress = getPrimaryAddress(customer)
  const openCount = context === 'bookings' ? customer.open_booking_count ?? 0 : customer.open_job_count ?? 0
  const completedCount = context === 'bookings' ? customer.completed_booking_count ?? 0 : customer.completed_job_count ?? 0
  const isSelfCustomer = String(customer.user_id ?? customer.id) === String(currentUserId ?? '')

  return (
    <div className="space-y-6 p-6">
      <ResetPasswordDialog
        open={isResetPasswordDialogOpen}
        onOpenChange={setIsResetPasswordDialogOpen}
        subjectName={getCustomerDisplayName(customer)}
        entityLabel="customer"
        isPending={resetPasswordMutation.isPending}
        onConfirm={(password) => resetPasswordMutation.mutate(password)}
      />
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-2">
          <Button variant="ghost" className="h-auto gap-2 px-0 text-muted-foreground" onClick={() => navigate(buildDirectoryLink())}>
            <ArrowLeft className="h-4 w-4" />
            Customers
          </Button>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{getCustomerDisplayName(customer)}</h1>
            <p className="text-sm text-muted-foreground">Customer basic information and orders</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canForcePasswordResetUsers ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setIsResetPasswordDialogOpen(true)}
              disabled={resetPasswordMutation.isPending || isSelfCustomer}
            >
              <KeyRound className="mr-2 h-4 w-4" />
              Reset password
            </Button>
          ) : null}
          <Badge variant="outline" className={getStatusBadgeClassName(customer.status)}>
            {customer.status || 'Unknown'}
          </Badge>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Users className="h-4 w-4" />
              Basic information
            </CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 text-sm sm:grid-cols-2">
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Name</p>
              <p className="font-medium text-foreground">{getCustomerDisplayName(customer)}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Customer type</p>
              <p>{customer.user_group_title || 'Customer'}</p>
            </div>
            {linkedEnquiries.length > 0 ? (
              <div className="flex items-start gap-2 sm:col-span-2">
                <FileText className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">
                    {linkedEnquiries.length === 1 ? 'Enquiry' : 'Enquiries'}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {linkedEnquiries.map((enquiry) => (
                      <Button key={enquiry.id} variant="link" className="h-auto p-0 font-medium" asChild>
                        <Link to={buildEnquiryLink(enquiry.id)}>
                          #{enquiry.id}
                          {enquiry.name ? ` · ${enquiry.name}` : ''}
                        </Link>
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
            ) : null}
            <div className="flex items-start gap-2">
              <Mail className="mt-0.5 h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">Email</p>
                <p>{firstPresent(customer.email)}</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Phone className="mt-0.5 h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">Phone</p>
                <p>{firstPresent(customer.phone)}</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <CalendarDays className="mt-0.5 h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">Created</p>
                <CompactTableDate value={customer.created_on} />
              </div>
            </div>
            <div className="flex items-start gap-2 sm:col-span-2">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">Primary address</p>
                {primaryAddress ? (
                  <>
                    <p className="font-medium text-foreground">{getAddressTypeLabel(primaryAddress.type)}</p>
                    <p>{formatAddress(primaryAddress)}</p>
                    {buildAddressAccessSummary(primaryAddress).map((line) => (
                      <p key={line} className="text-sm text-muted-foreground">{line}</p>
                    ))}
                  </>
                ) : (
                  <p>-</p>
                )}
              </div>
            </div>
            {isBusinessCustomer ? (
              <div className="flex items-start gap-2 sm:col-span-2">
                <CalendarDays className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">Business hours</p>
                  {primaryAddress && (primaryAddress.opening_time || primaryAddress.closing_time || primaryAddress.saturday_closed || primaryAddress.sunday_closed) ? (
                    <>
                      {(primaryAddress.opening_time || primaryAddress.closing_time) ? (
                        <p className="font-medium text-foreground">
                          {[formatTimeLabel(primaryAddress.opening_time), formatTimeLabel(primaryAddress.closing_time)].filter(Boolean).join(' – ')}
                        </p>
                      ) : null}
                      <p className="text-sm text-muted-foreground">
                        {[
                          primaryAddress.saturday_closed ? 'Closed Saturday' : 'Open Saturday',
                          primaryAddress.sunday_closed ? 'Closed Sunday' : 'Open Sunday',
                        ].join(' · ')}
                      </p>
                    </>
                  ) : (
                    <p className="text-sm text-muted-foreground">No business hours set. Add or edit a business address below.</p>
                  )}
                </div>
              </div>
            ) : null}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <CreditCard className="h-4 w-4" />
              Activity
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-md border border-border/70 p-3">
                <p className="text-xs text-muted-foreground">Open</p>
                <Button variant="link" className="h-auto p-0 text-lg font-semibold" asChild>
                  <Link to={context === 'bookings' ? buildBookingsLink('open') : buildJobsLink('open')}>{openCount}</Link>
                </Button>
              </div>
              <div className="rounded-md border border-border/70 p-3">
                <p className="text-xs text-muted-foreground">Completed</p>
                <Button variant="link" className="h-auto p-0 text-lg font-semibold" asChild>
                  <Link to={context === 'bookings' ? buildBookingsLink('completed') : buildJobsLink('completed')}>{completedCount}</Link>
                </Button>
              </div>
            </div>
            <div className="rounded-md border border-border/70 p-3">
              <p className="text-xs text-muted-foreground">Unpaid amount</p>
              <p className="text-lg font-semibold">{formatCurrency(Number(customer.total_unpaid_booking_amount ?? 0) || 0)}</p>
            </div>
            {context === 'bookings' ? (
              <>
                <div className="rounded-md border border-border/70 p-3">
                  <p className="text-xs text-muted-foreground">Credit outstanding</p>
                  <p className="text-lg font-semibold">
                    {formatCurrency(Number(customer.credit_outstanding_balance ?? 0) || 0)}
                  </p>
                </div>
                <div className="rounded-md border border-border/70 p-3">
                  <p className="text-xs text-muted-foreground">Total credit so far</p>
                  <p className="text-lg font-semibold">
                    {formatCurrency(Number(customer.total_credit_so_far ?? 0) || 0)}
                  </p>
                </div>
                <Button variant="outline" className="w-full" asChild>
                  <Link
                    to={withServiceInstanceId(
                      `/services/credits?user_id=${encodeURIComponent(String(customer.user_id ?? customer.id))}`,
                      serviceInstanceId,
                    )}
                  >
                    Open credit account
                  </Link>
                </Button>
              </>
            ) : null}
            <div className="rounded-md border border-border/70 p-3">
              <p className="text-xs text-muted-foreground">Current page order value</p>
              <p className="text-lg font-semibold">{formatCurrency(totalValue)}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <MapPin className="h-4 w-4" />
            Addresses
          </CardTitle>
        </CardHeader>
        <CardContent>
          {rawUserId ? (
            <OrgUserAddressManager
              userId={String(rawUserId)}
              entityDisplayName={getCustomerDisplayName(customer)}
              email={customer.email ?? null}
              phone={customer.phone ?? null}
              initialAddresses={toAddressArray(customer.address_data)}
              defaultAddressType={isBusinessCustomer ? USER_ADDRESS_TYPE.BUSINESS : USER_ADDRESS_TYPE.RESIDENTIAL}
              showBusinessHoursFields={isBusinessCustomer}
              canCreateAddresses={canCreateCustomerAddresses}
              canUpdateAddresses={canUpdateCustomerAddresses}
              canDeleteAddresses={canDeleteCustomerAddresses}
              canSetDefaultAddresses={canSetDefaultCustomerAddresses}
              entityLabel="customer"
              detailQueryKey={['customer-detail', customerId, serviceInstanceId, customerGroupIds]}
            />
          ) : (
            <p className="text-sm text-muted-foreground">Address information is unavailable because the customer user id is missing.</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle className="text-base">Orders list</CardTitle>
          <Button variant="outline" size="sm" asChild>
            <Link to={buildBookingsLink('open')}>View open bookings</Link>
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          {ordersQuery.isLoading ? (
            <div className="flex items-center gap-2 px-6 py-10 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading orders...
            </div>
          ) : orders.length === 0 ? (
            <div className="px-6 py-10 text-sm text-muted-foreground">No orders found for this customer.</div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table className={commerceTableClassName}>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Order</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Booking date</TableHead>
                      <TableHead>Service date</TableHead>
                      <TableHead>Payment</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orders.map((order) => (
                      <TableRow key={order.id || order.reference_id}>
                        <TableCell className="font-medium">
                          <Button variant="link" className="h-auto p-0" asChild>
                            <Link to={buildOrderDetailLink(order)}>{order.reference_id || order.id}</Link>
                          </Button>
                        </TableCell>
                        <TableCell className="text-muted-foreground">{getOrderTypeLabel(order)}</TableCell>
                        <TableCell><Badge variant="outline">{getOrderStatusLabel(order)}</Badge></TableCell>
                        <TableCell><CompactTableDate value={order.order_time ?? order.created_on} /></TableCell>
                        <TableCell><CompactTableDate value={order.delivery_time} /></TableCell>
                        <TableCell className="text-muted-foreground">{order.is_paid ? 'Paid' : 'Unpaid'}</TableCell>
                        <TableCell className="text-right text-muted-foreground">{formatCurrency(Number(order.total ?? 0) || 0)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className="border-t border-border/70 p-4">
                <DataTablePagination
                  page={page}
                  pageSize={pageSize}
                  totalCount={totalOrders}
                  onPageChange={setPage}
                  onPageSizeChange={(value) => {
                    setPageSize(value)
                    setPage(1)
                  }}
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}